import {ChangeDetectionStrategy, Component, inject, input, model, NgZone, OnChanges, output, signal, SimpleChanges} from '@angular/core';
import {animate, animateChild, group, query, state, style, transition, trigger} from '@angular/animations';
import {take} from 'rxjs/operators';
import {Router, RouterModule} from '@angular/router';
import {CompanyListModel, User} from '@core/services';
import {AccountCroppingService} from '@pages/account/account-cropping/account-cropping.service';
import {SidenavService} from './sidenav.service';
import {OctDocumentService} from '@services/document/document.service';
import {MatDrawerMode, MatSidenavModule} from '@angular/material/sidenav';
import {NavigationMode} from '@shared/enums';
import {WindowWidthDetectorDirective} from '@shared/directives';
import {toSignal} from '@angular/core/rxjs-interop';
import {InitialsPipe} from '@shared/pipes/initials';
import {MatTooltip} from '@angular/material/tooltip';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatMenuModule} from '@angular/material/menu';
import {NgClass} from '@angular/common';

@Component({
  selector: 'oct-sidenav',
  imports: [
    RouterModule,
    InitialsPipe,
    WindowWidthDetectorDirective,
    MatTooltip,
    MatIcon,
    MatIconButton,
    MatSidenavModule,
    MatMenuModule,
    NgClass
  ],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('animationSidenavState', [
      state('true', style({width: '260px'})),
      state('false', style({width: '80px'})),
      transition('true => false', [
        group([query('*', [animateChild()], {optional: true}), animate('400ms cubic-bezier(.25, .8, .25, 1)')])
      ]),
      transition('false => true', group([query('*', [animateChild()], {optional: true}), animate('400ms cubic-bezier(.25, .8, .25, 1)')]))
    ])
  ]
})
export class SidenavComponent implements OnChanges {
  navigationMode = NavigationMode;
  modeSidenav: MatDrawerMode = this.navigationMode.side;
  disableTooltip = signal(false);
  image = toSignal(inject(AccountCroppingService).imageSubject);
  sidenavGeneralState = signal(true);
  sidenavChangeState = model(true);
  sidenavOpenedState = model(true);
  urlPrefix = input.required<string>();
  companyData = input.required<CompanyListModel>();
  companies = input.required<CompanyListModel[]>();
  currentUser = input.required<User>();
  readonly emitSidenavOpenedState = output<boolean>();
  readonly emitSidenavChangeState = output<boolean>();
  readonly sidenavModeEmitter = output<string>();
  readonly companyPrefixEmit = output<string>();
  private _breakpointExceeded!: boolean;
  private readonly _sidenavService = inject(SidenavService);
  private readonly _document = inject(OctDocumentService);
  private readonly _ngZone = inject(NgZone);
  private readonly _router = inject(Router);

  navigateToAccount() {
    this._router.navigate(['account/personal-info']);
    this._closeModeOverSidenav();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.sidenavChangeState) {
      this.toggleSidenavGeneralState();
      this._changeGeneralSidenavState(this._breakpointExceeded);
      this._ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.sidenavChangeState.set(true);
        this.emitSidenavChangeState.emit(this.sidenavChangeState());
      });
    }
  }

  /** Toggle state sidenav for desktop version  (incomplete folding of the element) */
  toggleSidenavState() {
    this.sidenavOpenedState.set(!this.sidenavOpenedState());
    this.emitSidenavOpenedState.emit(this.sidenavOpenedState());
    this._sidenavService.onViewChange.next(this.sidenavOpenedState());
  }

  /** Toggle state sidenav for a tablet and mobile version. Open/close all sidenav. */
  toggleSidenavGeneralState() {
    this.sidenavGeneralState.set(!this.sidenavGeneralState());
    if (!this.sidenavOpenedState()) {
      this.sidenavOpenedState.set(true);
      this.emitSidenavOpenedState.emit(this.sidenavOpenedState());
    }
    this._sidenavService.onViewChange.next(this.sidenavOpenedState());
  }

  toggleCompany(company: CompanyListModel) {
    if (company?.attributes?.urlPrefix && this.urlPrefix() !== company?.attributes?.urlPrefix) {
      this._document.reload(company.attributes.urlPrefix);
    }
  }

  animationDone() {
    this._sidenavService.sidenavState.next(this.sidenavOpenedState);
  }

  handleWindowWidthStatus(isDesktopSize: boolean) {
    this._breakpointExceeded = isDesktopSize;
    this._changeGeneralSidenavState(this._breakpointExceeded);
  }

  /** Check state sidenav depend on screen width and previous type of version */
  private _changeGeneralSidenavState(isDesktop: boolean) {
    this.sidenavGeneralState.set(isDesktop);
    this.modeSidenav = isDesktop ? this.navigationMode.side : this.navigationMode.over;
    this.disableTooltip.set(!isDesktop);
    this.sidenavModeEmitter.emit(this.modeSidenav);
  }

  private _closeModeOverSidenav() {
    if (this.modeSidenav === this.navigationMode.over) {
      this.toggleSidenavGeneralState();
    }
  }
}
